import { useState, useEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { getAppLocale } from "@/utils/appLocale";
import type { RecipePreview } from "@/types/recipePreview";
import { TAB_NAV_STALE_MS } from "@/utils/reactQueryTabNav";

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const DEBOUNCE_MS = 80;

function isValidUUID(s: string): boolean {
  return UUID_REGEX.test(s);
}

function stableIdsKey(recipeIds: string[]): string {
  return [...new Set(recipeIds)].filter(isValidUUID).sort().join(",");
}

function nutritionGoalsFromRpc(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter((g): g is string => typeof g === "string");
}

function toRecipePreview(row: {
  id: string;
  title: string | null;
  description: string | null;
  cooking_time_minutes: number | null;
  min_age_months: number | null;
  max_age_months: number | null;
  ingredient_names: string[] | null;
  ingredient_total_count: number | null;
  is_favorite?: boolean | null;
  nutrition_goals?: unknown;
}): RecipePreview {
  return {
    id: row.id,
    title: row.title ?? "",
    description: row.description ?? null,
    cookTimeMinutes: row.cooking_time_minutes ?? null,
    ingredientNames: Array.isArray(row.ingredient_names) ? row.ingredient_names : [],
    ingredientTotalCount: typeof row.ingredient_total_count === "number" ? row.ingredient_total_count : 0,
    minAgeMonths: row.min_age_months ?? null,
    maxAgeMonths: row.max_age_months ?? null,
    isFavorite: !!row.is_favorite,
    nutrition_goals: nutritionGoalsFromRpc(row.nutrition_goals),
  };
}

/**
 * Fetches recipe previews by IDs. Filters to valid UUIDs only (non-UUIDs are skipped).
 * IDs are stabilized (unique + sort) and debounced (80ms) so rapid changes during generation don't trigger many requests.
 * @param recipeIds - recipe UUIDs
 * @param locale - optional locale for title/description (e.g. 'ru', 'en'); when not set, getAppLocale() is used
 */
export function useRecipePreviewsByIds(recipeIds: string[], locale?: string | null) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const inputKey = useMemo(() => stableIdsKey(recipeIds), [recipeIds]);
  const [debouncedKey, setDebouncedKey] = useState(inputKey);
  const effectiveLocale = locale ?? getAppLocale();

  useEffect(() => {
    if (inputKey === debouncedKey) return;
    if (!debouncedKey && inputKey) {
      setDebouncedKey(inputKey);
      return;
    }
    const t = setTimeout(() => setDebouncedKey(inputKey), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [inputKey, debouncedKey]);

  const ids = useMemo(() => (debouncedKey ? debouncedKey.split(",").filter(Boolean) : []), [debouncedKey]);

  const query = useQuery({
    queryKey: ["recipe_previews", user?.id, debouncedKey, effectiveLocale],
    queryFn: async (): Promise<Record<string, RecipePreview>> => {
      if (!user || ids.length === 0) return {};
      const [previewsResult, tipsResult] = await Promise.all([
        supabase.rpc("get_recipe_previews", { recipe_ids: ids, p_locale: effectiveLocale }),
        supabase
          .from("recipes")
          .select("id, chef_advice, advice, source, calories, proteins, fats, carbs, nutrition_goals")
          .in("id", ids),
      ]);
      const { data: previewRows, error } = previewsResult;
      if (error) throw error;
      const tipsRows = (tipsResult.data ?? []) as Array<{
        id: string;
        chef_advice?: string | null;
        advice?: string | null;
        source?: string | null;
        calories?: number | null;
        proteins?: number | null;
        fats?: number | null;
        carbs?: number | null;
        nutrition_goals?: unknown;
      }>;
      const tipsMap = new Map(
        tipsRows.map((r) => [
          r.id,
          {
            chefAdvice: r.chef_advice ?? null,
            advice: r.advice ?? null,
            source: r.source ?? null,
            calories: r.calories ?? null,
            proteins: r.proteins ?? null,
            fats: r.fats ?? null,
            carbs: r.carbs ?? null,
            nutrition_goals: r.nutrition_goals,
          },
        ])
      );
      const rows = (previewRows ?? []) as Array<{
        id: string;
        title: string | null;
        description: string | null;
        cooking_time_minutes: number | null;
        min_age_months: number | null;
        max_age_months: number | null;
        ingredient_names: string[] | null;
        ingredient_total_count: number | null;
        is_favorite?: boolean | null;
        nutrition_goals?: unknown;
      }>;
      const map: Record<string, RecipePreview> = {};
      rows.forEach((r) => {
        const preview = toRecipePreview(r);
        const tips = tipsMap.get(r.id);
        const goalsFromRpc = nutritionGoalsFromRpc(r.nutrition_goals);
        const goalsFromTable = tips ? nutritionGoalsFromRpc(tips.nutrition_goals) : [];
        /** Пока RPC без колонки (миграция не применена), цели только из recipes. */
        preview.nutrition_goals = goalsFromRpc.length > 0 ? goalsFromRpc : goalsFromTable;
        if (tips) {
          preview.chefAdvice = tips.chefAdvice;
          preview.advice = tips.advice;
          preview.source = tips.source;
          preview.calories = tips.calories ?? undefined;
          preview.proteins = tips.proteins ?? undefined;
          preview.fats = tips.fats ?? undefined;
          preview.carbs = tips.carbs ?? undefined;
        }
        map[r.id] = preview;
      });
      return map;
    },
    enabled: !!user && ids.length > 0,
    staleTime: TAB_NAV_STALE_MS,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const invalidatePreviews = () => {
    queryClient.invalidateQueries({ queryKey: ["recipe_previews"] });
  };

  return {
    previews: query.data ?? {},
    isLoading: query.isLoading,
    error: query.error,
    invalidatePreviews,
  };
}
